import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ArrowRight, ChevronLeft, ChevronRight } from "lucide-react";

interface SetupStep {
  number: number;
  title: string;
  description: string;
  action: string;
  route: string;
  color: string; // For the color scheme
}

interface OnboardingTourCardsProps {
  layout?: "horizontal" | "vertical"; // default horizontal; vertical for lists
  emphasis?: "normal" | "large"; // larger fonts for modal use
  numberPosition?: "top" | "left"; // position of step number indicator
  showHeaderTitle?: boolean; // control rendering the component title
}

export default function OnboardingTourCards({
  layout = "horizontal",
  emphasis = "normal",
  numberPosition = "top",
  showHeaderTitle = true,
}: OnboardingTourCardsProps) {
  const navigate = useNavigate();
  const [showLeftArrow, setShowLeftArrow] = useState(false);
  const [showRightArrow, setShowRightArrow] = useState(false);

  // Define color scheme - dark blue, light blue, ruby red cycling
  const colorScheme = {
    darkBlue: "#1e40af", // Dark blue
    lightBlue: "#3b82f6", // Light blue (also used for Genie avatar)
    rubyRed: "#D6409F", // Ruby red (matching navigation ADMIN badge)
  };

  const setupSteps: SetupStep[] = [
    {
      number: 1,
      title: "Create Your Genie",
      description: "Set up your Genie with name, voice and personality",
      action: "Create Genie",
      route: "/user-hub/genie-setup",
      color: colorScheme.darkBlue,
    },
    {
      number: 2,
      title: "Add Knowledge",
      description: "Upload documents, FAQs & guides to train your genie",
      action: "Add Docs",
      route: "/genie-hub/knowledge-base",
      color: colorScheme.lightBlue,
    },
    {
      number: 3,
      title: "Set Genie Insights",
      description: "Define what information to capture from conversations",
      action: "Set Insights",
      route: "/genie-hub/my-genies",
      color: colorScheme.rubyRed,
    },
    {
      number: 4,
      title: "Customize Branding",
      description: "Add your colors, logo & style to match your brand",
      action: "Customize",
      route: "/genie-hub/my-genies",
      color: colorScheme.darkBlue,
    },
    {
      number: 5,
      title: "Distribute & Deploy",
      description: "Share via web embed, link, QR code or phone number",
      action: "Deploy",
      route: "/genie-hub/my-genies",
      color: colorScheme.lightBlue,
    },
  ];

  // Arrow visibility based on scroll state (md+), only for horizontal layout
  useEffect(() => {
    if (layout !== "horizontal") return;
    const container = document.getElementById("onboarding-steps-container");
    if (!container) return;

    const checkScroll = () => {
      setShowLeftArrow(container.scrollLeft > 0);
      setShowRightArrow(container.scrollLeft < container.scrollWidth - container.clientWidth - 10);
    };

    checkScroll();
    container.addEventListener("scroll", checkScroll);
    window.addEventListener("resize", checkScroll);

    return () => {
      container.removeEventListener("scroll", checkScroll);
      window.removeEventListener("resize", checkScroll);
    };
  }, [layout]);

  const scrollContainer = (direction: "left" | "right") => {
    const container = document.getElementById("onboarding-steps-container");
    if (!container) return;
    const scrollAmount = Math.min(260, container.clientWidth - 48);
    container.scrollBy({
      left: direction === "left" ? -scrollAmount : scrollAmount,
      behavior: "smooth",
    });
  };

  const isLarge = emphasis === "large";
  const numberLeft = numberPosition === "left";

  return (
    <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-4">
      {showHeaderTitle && (
        <div className="flex items-center justify-between mb-4">
          <h3 className={`${isLarge ? "text-xl font-semibold" : "text-sm font-medium"} text-gray-700`}>
            How to Set Up Your Genie
          </h3>
        </div>
      )}

      <div className="relative">
        {/* Arrow controls with dynamic visibility */}
        {layout === "horizontal" && showLeftArrow && (
          <div className="hidden md:flex pointer-events-none absolute inset-y-0 left-0 w-12 z-10 items-center">
            <button
              aria-label="Scroll left"
              onClick={(e) => {
                e.stopPropagation();
                scrollContainer("left");
              }}
              className="pointer-events-auto mx-1 w-9 h-9 bg-white/95 backdrop-blur rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 active:scale-95 transition"
            >
              <ChevronLeft className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}
        {layout === "horizontal" && showRightArrow && (
          <div className="hidden md:flex pointer-events-none absolute inset-y-0 right-0 w-12 z-10 items-center justify-end">
            <button
              aria-label="Scroll right"
              onClick={(e) => {
                e.stopPropagation();
                scrollContainer("right");
              }}
              className="pointer-events-auto mx-1 w-9 h-9 bg-white/95 backdrop-blur rounded-full shadow-lg flex items-center justify-center hover:bg-gray-50 active:scale-95 transition"
            >
              <ChevronRight className="w-4 h-4 text-gray-600" />
            </button>
          </div>
        )}

        {/* Layout: vertical list or horizontal scroller */}
        <div
          id={layout === "horizontal" ? "onboarding-steps-container" : undefined}
          className={
            layout === "vertical"
              ? "flex flex-col gap-3 pt-4"
              : "grid grid-cols-1 sm:grid-cols-2 md:flex gap-3 md:overflow-x-auto scrollbar-hide md:pb-2 pt-4 md:-mx-2 md:px-2 md:snap-x md:snap-mandatory md:snap-none"
          }
          style={
            layout === "vertical"
              ? undefined
              : {
                  scrollbarWidth: "none",
                  msOverflowStyle: "none",
                  WebkitOverflowScrolling: "touch",
                  touchAction: "pan-x",
                  overscrollBehaviorX: "contain",
                }
          }
        >
          {setupSteps.map((step) => (
            <div
              className={layout === "vertical" ? "relative w-full" : "relative w-full md:min-w-[190px] md:snap-start"}
              key={step.number}
            >
              {/* Step number indicator */}
              {!numberLeft && (
                <div
                  className="absolute -top-3 left-1/2 -translate-x-1/2 w-7 h-7 text-white rounded-full flex items-center justify-center text-xs font-bold z-10"
                  style={{ backgroundColor: step.color }}
                >
                  {step.number}
                </div>
              )}

              <div
                onClick={() => navigate(step.route)}
                className="group flex flex-col p-3 pt-3 rounded-xl bg-white border-3 hover:shadow-lg transition-all duration-200 cursor-pointer h-[128px]"
                style={{
                  borderColor: `${step.color}40`,
                }}
              >
                <div className={`flex-1 ${numberLeft ? "flex items-start gap-3" : ""}`}>
                  {numberLeft && (
                    <div
                      className="flex items-center justify-center rounded-full text-white font-bold"
                      style={{ width: isLarge ? 40 : 28, height: isLarge ? 40 : 28, backgroundColor: step.color }}
                    >
                      <span className={isLarge ? "text-sm" : "text-xs"}>{step.number}</span>
                    </div>
                  )}
                  <div className="flex-1 min-w-0">
                    <h4 className={`${isLarge ? "text-base" : "text-sm"} font-semibold text-gray-900 mb-1`}>
                      {step.title}
                    </h4>
                    <p className={`${isLarge ? "text-sm" : "text-xs"} text-gray-500 line-clamp-2`}>
                      {step.description}
                    </p>
                  </div>
                </div>

                {/* Footer action */}
                <div className="mt-auto pt-2 border-t border-gray-100">
                  <div
                    className={`flex items-center font-medium transition-colors ${isLarge ? "text-sm" : "text-xs"}`}
                    style={{ color: step.color }}
                  >
                    {step.action}
                    <ArrowRight className="h-3 w-3 ml-1" />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
